import React from 'react';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { Field, FieldError, Input } from '../../../form-kit';
import { Collapse } from '../../../../motion';
import {
  CLEANING_DONE_CHOICES,
  CLEANING_KIND_CHOICES,
  CLEANING_NOTES_MAX,
  FormContext,
} from './reservation-form-model';
import { describe, errorId, FieldKit, FieldLabel } from './reservation-fields';
import { ControlStack, FormSection, SectionTitle } from './reservation-form.styled';

/**
 * "CLEANING" (plan S4, R-S4.10): not scheduled, or done by an agency (with its
 * fee, pre-filled from the listing's default) or by the host. Once someone is
 * doing it: scheduled or cleaned. The server answers every cleaning refusal
 * under the one key `cleaning`; it shows under the controls.
 */

export const CleaningFields: React.FC<{ kit: FieldKit; ctx: FormContext; headingId: string }> = ({ kit, ctx, headingId }) => {
  const { form, errors, disabled, update, blur, id } = kit;
  const someone = form.cleaning_kind !== 'none';

  return (
    <FormSection $flat={kit.flat} aria-labelledby={headingId}>
      <SectionTitle id={headingId}>Cleaning</SectionTitle>
      <ControlStack id={id('cleaning')}>
        <FieldLabel kit={kit} field="cleaning_kind" id={`${id('cleaning')}-label`}>
          Who cleans
        </FieldLabel>
        <SegmentedControl
          options={CLEANING_KIND_CHOICES}
          value={form.cleaning_kind}
          label="Who cleans"
          disabled={disabled}
          onChange={value => update({ cleaning_kind: value })}
        />
      </ControlStack>

      <Collapse open={someone} id={`${id('cleaning')}-detail`}>
        {/* A co-host books the agency at the listing's own fee (IS5: no amounts). */}
        {form.cleaning_kind === 'agency' && !ctx.hideMoney ? (
          <Field>
            <FieldLabel kit={kit} field="cleaning_fee" htmlFor={id('cleaning_fee')}>
              {`Agency fee (${ctx.currency})`}
            </FieldLabel>
            <Input
              id={id('cleaning_fee')}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={form.cleaning_fee}
              disabled={disabled}
              aria-invalid={errors.cleaning_fee ? true : undefined}
              aria-describedby={describe(kit, 'cleaning_fee')}
              hasError={!!errors.cleaning_fee}
              onChange={event => update({ cleaning_fee: event.target.value })}
              onBlur={() => {
                if (blur) blur('cleaning_fee');
              }}
            />
            <FieldError id={errorId(kit, 'cleaning_fee')} message={errors.cleaning_fee} />
          </Field>
        ) : null}
        <ControlStack>
          <FieldLabel kit={kit} field="cleaning_done">
            Cleaning status
          </FieldLabel>
          <SegmentedControl
            options={CLEANING_DONE_CHOICES}
            value={form.cleaning_done}
            label="Cleaning status"
            disabled={disabled}
            onChange={value => update({ cleaning_done: value })}
          />
        </ControlStack>
      </Collapse>
      <FieldError id={errorId(kit, 'cleaning')} message={errors.cleaning} />

      <Field>
        <FieldLabel kit={kit} field="cleaning_notes" htmlFor={id('cleaning_notes')}>
          Cleaning notes
        </FieldLabel>
        <CountedTextarea
          id={id('cleaning_notes')}
          value={form.cleaning_notes}
          max={CLEANING_NOTES_MAX}
          rows={2}
          disabled={disabled}
          hasError={!!errors.cleaning_notes}
          aria-describedby={errors.cleaning_notes ? errorId(kit, 'cleaning_notes') : undefined}
          onChange={event => update({ cleaning_notes: event.target.value })}
        />
        <FieldError id={errorId(kit, 'cleaning_notes')} message={errors.cleaning_notes} />
      </Field>
    </FormSection>
  );
};
